import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { authApi, type AppName, type SessionUser } from '@ps/api-client/auth';
import { ApiError, SESSION_EXPIRED_EVENT } from '@ps/api-client/http';

/**
 * Session state for one app, shared by all three apps.
 * - "Not signed in" is a normal state (null), not an error.
 * - Sign-out marks the session ended FIRST, then clears cached data, so the screen switches immediately.
 * - If the sign-out request fails, the user stays signed in and is told: the server session is still valid,
 *   so pretending otherwise would be unsafe.
 */
export function useSession(app: AppName) {
  const qc = useQueryClient();
  const key = ['session', app] as const;
  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState<string | null>(null);

  const query = useQuery<SessionUser | null>({
    queryKey: key,
    queryFn: async () => {
      try { return (await authApi.session(app)).user; }
      catch (e) { if (e instanceof ApiError && e.status === 401) return null; throw e; }
    },
  });

  const endLocally = useCallback(() => {
    qc.setQueryData(key, null);                                         // screen switches to sign-in now
    qc.removeQueries({ predicate: q => q.queryKey[0] !== 'session' });  // then drop everything else cached
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qc, app]);

  useEffect(() => {
    window.addEventListener(SESSION_EXPIRED_EVENT, endLocally);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, endLocally);
  }, [endLocally]);

  const signOut = useCallback(async () => {
    setSigningOut(true);
    setSignOutError(null);
    try {
      await authApi.logout(app);
      endLocally();
    } catch {
      setSignOutError("We couldn't sign you out. Check your connection and try again.");
    } finally {
      setSigningOut(false);
    }
  }, [app, endLocally]);

  const signedIn = useCallback((user: SessionUser) => { qc.setQueryData(key, user); setSignOutError(null); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc, app]);

  /** Company selector: switch the session user and drop every cached screen of the previous company. */
  const companyChanged = useCallback((user: SessionUser) => {
    qc.setQueryData(key, user);
    qc.removeQueries({ predicate: q => q.queryKey[0] !== 'session' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qc, app]);

  return {
    user: query.data ?? null,
    isChecking: query.isPending,
    checkError: query.error,
    retryCheck: () => query.refetch(),
    signedIn, companyChanged, signOut, signingOut, signOutError,
  };
}
